import config from "../lib/config";
import { getRecentPlayAccountKey } from "./recentPlayHistory";

export const FAVORITES_UPDATED_EVENT = "crypto-favorites-updated";

const STORAGE_PREFIX = "crypto_favorite_games";
const MAX_FAVORITE_ITEMS = 48;

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${config.backendUrl}/${trimLeadingSlash(trimmed)}`;
};

const uniqueUrls = (urls) => Array.from(new Set(urls.filter(Boolean)));

const normalizeText = (value = "") =>
  String(value || "")
    .replace(/\bPRAGMATIC\b/gi, "프라그마틱")
    .replace(/\bEVOLUTION\b/gi, "에볼루션")
    .replace(/\bMICROGAMING\b/gi, "마이크로게이밍")
    .replace(/\bBTI\b/gi, "비티아이")
    .replace(/\bBT1\b/gi, "비티원")
    .replace(/\bSABA\b/gi, "사바")
    .replace(/\bSA(?=게이밍|카지노|스포츠|\s|$)/gi, "에스에이")
    .replace(/\s+/g, " ")
    .trim();

const resolveAccountKey = (userOrAccountKey) =>
  typeof userOrAccountKey === "string"
    ? userOrAccountKey
    : getRecentPlayAccountKey(userOrAccountKey);

const getStorageKey = (accountKey) => `${STORAGE_PREFIX}:${accountKey}`;

const readRows = (accountKey) => {
  if (typeof window === "undefined" || !accountKey) return [];

  try {
    const parsed = JSON.parse(localStorage.getItem(getStorageKey(accountKey)) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeRows = (accountKey, rows) => {
  if (typeof window === "undefined" || !accountKey) return;

  try {
    localStorage.setItem(
      getStorageKey(accountKey),
      JSON.stringify(rows.slice(0, MAX_FAVORITE_ITEMS))
    );
  } catch {
    // Favorites are a UI convenience and must not block game launch flows.
  }
};

const dispatchFavoritesUpdated = (accountKey, rows, changedKey, favorited) => {
  if (typeof window === "undefined") return;

  try {
    window.dispatchEvent(
      new CustomEvent(FAVORITES_UPDATED_EVENT, {
        detail: { accountKey, rows, changedKey, favorited },
      })
    );
  } catch {
    // Event dispatch only refreshes mounted UI.
  }
};

const getVendorImage = (vendor) =>
  uniqueUrls([
    resolveAssetUrl(vendor?.image),
    resolveAssetUrl(vendor?.pc_image),
    resolveAssetUrl(vendor?.mobile_image),
    resolveAssetUrl(vendor?.thumbnail),
    resolveAssetUrl(vendor?.vendor_list?.thumbnails),
  ])[0] || "";

const getGameImage = (game) =>
  resolveAssetUrl(
    game?.game_image ||
      game?.image ||
      game?.thumbnail ||
      game?.thumbnail_url ||
      game?.pc_image ||
      game?.mobile_image
  );

export const createFavoriteProviderItem = (vendor = {}, options = {}) => {
  const lobbyId = vendor?.vendor_list?.lobby_id || vendor?.lobby_id || options.lobbyId || "";
  const vendorName = vendor?.vendor_name || options.vendorName || "";
  const gameType = options.gameType || vendor?.game_type || vendor?.type || "";
  const title = normalizeText(
    options.label ||
      vendor?.vendor_name_kr ||
      vendor?.vendor_name_ko ||
      vendor?.display_name ||
      vendorName ||
      "게임사"
  );
  const kind = options.kind || (options.href ? "slot-provider" : "provider");

  return {
    key: `${kind}:${gameType || "game"}:${lobbyId || vendorName || title}`,
    kind,
    gameType,
    lobbyId,
    vendorName,
    title,
    image: options.image || getVendorImage(vendor),
    href: options.href || "",
    meta: options.meta || "게임사",
  };
};

export const createFavoriteGameItem = (game = {}, options = {}) => {
  const gameId = game?.game_id || game?.id || options.gameId || "";
  const vendorName = game?.vendor || game?.vendor_name || options.vendorName || "";
  const title = normalizeText(
    options.label ||
      game?.game_name_ko ||
      game?.game_name_kr ||
      game?.name_ko ||
      game?.display_name ||
      game?.game_name ||
      game?.name ||
      "게임"
  );

  return {
    key: `game:${gameId || `${vendorName}:${title}`}`,
    kind: options.kind || "slot-game",
    gameType: options.gameType || game?.game_type || "slots",
    gameId,
    lobbyId: options.lobbyId || "",
    vendorName,
    title,
    image: options.image || getGameImage(game),
    href: options.href || "",
    meta: options.meta || vendorName || "게임",
  };
};

const normalizeFavoriteItem = (item) => {
  if (!item?.key || !item?.title) return null;

  return {
    key: item.key,
    kind: item.kind || "slot-game",
    gameType: item.gameType || "",
    gameId: item.gameId || "",
    lobbyId: item.lobbyId || "",
    vendorName: item.vendorName || "",
    title: normalizeText(item.title),
    image: item.image || "",
    href: item.href || "",
    meta: normalizeText(item.meta || "즐겨찾기"),
    updatedAt: Date.now(),
  };
};

export const getFavoriteGames = (userOrAccountKey) => readRows(resolveAccountKey(userOrAccountKey));

export const isFavoriteGame = (userOrAccountKey, key) =>
  Boolean(key && getFavoriteGames(userOrAccountKey).some((item) => item?.key === key));

export const toggleFavoriteGame = (userOrAccountKey, item) => {
  const accountKey = resolveAccountKey(userOrAccountKey);
  const normalized = normalizeFavoriteItem(item);

  if (!accountKey || !normalized) {
    return { favorited: false, rows: readRows(accountKey) };
  }

  const current = readRows(accountKey);
  const alreadyFavorited = current.some((row) => row?.key === normalized.key);
  const nextRows = alreadyFavorited
    ? current.filter((row) => row?.key !== normalized.key)
    : [normalized, ...current.filter((row) => row?.key !== normalized.key)].slice(
        0,
        MAX_FAVORITE_ITEMS
      );

  writeRows(accountKey, nextRows);
  dispatchFavoritesUpdated(accountKey, nextRows, normalized.key, !alreadyFavorited);

  return { favorited: !alreadyFavorited, rows: nextRows };
};

export const buildVendorFromFavorite = (item = {}) => ({
  vendor_name: item.vendorName,
  vendor_name_kr: item.title,
  lobby_id: item.lobbyId,
  vendor_list: {
    lobby_id: item.lobbyId,
    thumbnails: item.image,
  },
  image: item.image,
  game_type: item.gameType,
});
